<template>
  <div class="seat-map-ga-row">
    <div class="seat-map-ga-row-content">
      <div>
        <v-list-item-title>
          {{ eventPrice.description }}
        </v-list-item-title>
        <v-list-item-subtitle v-if="!hideServiceFee">
          {{ formatCurrency(eventPrice.price) }} +
          <div class="ml-3">
            +
            <span v-if="staticData.isPublicUser" class="text-no-wrap">
              {{ formatCurrency(eventPrice.serviceFee) }} Service Fee
            </span>
            <span v-else>
              {{ formatCurrency(eventPrice.agentServiceFee) }} Agent Service Fee
            </span>
            <span
              ><br />
              + {{ formatCurrency(eventPrice.convenienceFeeAmount ?? 0) }} Conv.
              Fee</span
            >
            <span
              ><br />
              + {{ formatCurrency(eventPrice.salesTaxAmount ?? 0) }} Tax</span
            >
            <div>
              {{ formatCurrency(eventPrice.totalSingle ?? 0) }} per ticket after
              taxes and fees
            </div>
          </div>
        </v-list-item-subtitle>
        <v-list-item-subtitle v-else>
          {{
            formatCurrency(
              eventPrice.price! +
                (staticData.isPublicUser
                  ? eventPrice.serviceFee!
                  : eventPrice.agentServiceFee!),
            )
          }}
        </v-list-item-subtitle>
        <v-list-item-subtitle>
          <span v-if="eventPrice.minimumQuantity" class="text-no-wrap">
            Min Qty: {{ eventPrice.minimumQuantity }}
          </span>
        </v-list-item-subtitle>
        <v-list-item-subtitle>
          <span v-if="eventPrice.maximumPurchaseQuantity" class="text-no-wrap">
            Max Qty: {{ eventPrice.maximumPurchaseQuantity }}
          </span>
        </v-list-item-subtitle>
        <v-list-item-subtitle>
          <span v-if="showLowInventoryAlert" class="low-inventory-alert">
            {{ props.availableCount }} Remaining
          </span>
        </v-list-item-subtitle>
      </div>
      <div
        :class="`d-flex ${display.mdAndDown ? '' : 'align-center justify-end'}`"
      >
        <v-list-item-action
          :class="`${display.mdAndDown ? 'pt-3 mx-auto' : ''}`"
        >
          <div class="d-flex flex-column align-center">
            <quantity-input
              :value="localQuantity"
              @update="(val) => onQuantityInput(val)"
              @value-out-of-bounds="
                (val, min, max) => onValueOutOfBounds(val, min, max)
              "
              :max="effectiveMax"
              :zero-on-min="true"
              :min="eventPrice.minimumQuantity || 0"
              :size="resolveSmallComponentSize"
              :disabled="isUpdatingQuantity"
              :disable-plus="soldOut || localQuantity >= effectiveMax"
              :loading="isUpdatingQuantity"
            />
            <div
              v-if="soldOut"
              class="text-caption text-error font-weight-bold mt-1"
            >
              Sold Out
            </div>
          </div>
        </v-list-item-action>
      </div>
      <div class="text-body-2 text-red" v-if="outOfBoundsMessage">
        {{ outOfBoundsMessage }}
      </div>
    </div>

    <!-- Surface a failed add/remove (e.g. blocked from another session) on this row. -->
    <c-loader-status
      :loaders="{
        '': [service.addMultipleTickets, service.removeMultipleTickets],
      }"
      :progress-placeholder="false"
    />
  </div>
</template>

<script setup lang="ts">
// Remove the isAnotherRowSelected computed property since it's now handled by the parent
import { ref, computed, watch, toRef } from "vue";
import { EventPriceDto } from "@/models.g";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@/viewmodels.g";
import QuantityInput from "@common/components/quantity-input.vue";
import { formatCurrency } from "@common/utils";
import { useSeatingTicketChange } from "@common/composables/useSeatingTicketChange";
import useAccessibility from "@common/composables/useAccessibility";
import { useDisplay } from "vuetify";
import { ProvideInjectConsts } from "@common/provideInjectConsts";
import { debounce } from "lodash-es";

const { resolveSmallComponentSize } = useAccessibility();
const display = useDisplay();

const props = defineProps<{
  eventPrice: EventPriceDto;
  purchase: TicketPurchaseDtoViewModel;
  sectionName: string;
  sectionId: number | null;
  availableCount: number | null;
  seatNumber: string;
  highlightedPriceId: number | null;
  highlightedSectionName: string | null;
  isMobile: boolean;
  staticData: any;
  hideServiceFee: boolean;
  handleGAQtyFocus: (eventPrice: EventPriceDto, sectionName: string) => void;
  handleSelectPrice: (eventPrice: EventPriceDto, sectionName: string) => void;
}>();

const localQuantity = ref(0);
const pendingQuantity = ref<number | null>(null);
const isUpdatingQuantity = ref(false);
const outOfBoundsMessage = ref<string | null>(null);
const soldOut = computed(() => !!props.eventPrice.isSoldOut);

const showLowInventoryAlert = computed(() => {
  const threshold = props.eventPrice.lowInventoryThreshold ?? 0;
  const available = props.availableCount;

  // Show alert if threshold > 0 AND available > 0 AND available <= threshold
  // When available is 0, let the "Sold Out" message display instead
  return (
    threshold > 0 &&
    available !== null &&
    available > 0 &&
    available <= threshold &&
    !soldOut.value
  );
});

const service = new PublicPurchaseServiceViewModel();
const { handleQuantityUpdate } = useTicketChange(
  props.purchase,
  localQuantity,
  pendingQuantity,
);
const { onUpdateGACart } = useSeatingTicketChange(
  toRef(() => props.purchase),
  service,
);

// Injects function from parent component to register checkout callbacks
const registerCheckoutCallback = inject(
  ProvideInjectConsts.RegisterCheckoutCallback,
) as (id: string, fn: () => Promise<boolean>) => void;

// Injects function from parent component to un-register checkout callbacks
const unregisterCheckoutCallback = inject(
  ProvideInjectConsts.UnRegisterCheckoutCallback,
) as (id: string) => void;

onMounted(() => {
  registerCheckoutCallback(
    `qty-selector-sm-ga-${props.eventPrice.id}`,
    checkoutCallback,
  );
});

onUnmounted(() => {
  unregisterCheckoutCallback(`qty-selector-sm-ga-${props.eventPrice.id}`);
});

const checkoutCallback = async () => {
  // availableCount < 0 means our reservation was partially cancelled by concurrent
  // purchases — reduce to the corrected maximum before proceeding.
  if (props.availableCount !== null && props.availableCount < 0) {
    const correctedQuantity = Math.max(
      0,
      localQuantity.value + props.availableCount,
    );
    outOfBoundsMessage.value = `Quantity reduced to ${correctedQuantity} — that's all that's available in this section.`;
    localQuantity.value = correctedQuantity;
    pendingQuantity.value = correctedQuantity;
    setTimeout(() => {
      outOfBoundsMessage.value = null;
    }, 5000);
    await updateQuantity();
    return false;
  }
  await updateQuantity();
  return true;
};

const tickets = computed(() => {
  return (
    props.purchase.tickets?.filter(
      (t) =>
        t.eventPriceId === props.eventPrice.id &&
        t.seatNumber?.includes(props.seatNumber),
    ) || []
  );
});

const currentQuantity = computed(() => tickets.value.length);

// effectiveMax = min(sectionCapacity, perPersonCap)
//
// sectionCapacity = currentQuantity + availableCount
//   Both values come from the same server response, so their sum equals the true
//   section capacity and stays stable during rapid clicking (unlike localQuantity).
//
// perPersonCap = eventPrice.maximumPurchaseQuantity ?? 999999
//   We intentionally do NOT use getMaxQuantity / eventDate.availableSeats here.
//   availableSeats is a GLOBAL count that decreases as the user adds tickets (it
//   counts their own reservations), so using it as effectiveMax would shrink the
//   cap below the user's current quantity and incorrectly block decrements.
const effectiveMax = computed(() => {
  const sectionMax =
    props.availableCount !== null
      ? currentQuantity.value + props.availableCount
      : 999999;
  const perPersonMax = props.eventPrice.maximumPurchaseQuantity || 999999;
  return Math.min(sectionMax, perPersonMax);
});

const hasPendingChange = computed(() => {
  return (
    pendingQuantity.value !== null &&
    pendingQuantity.value !== currentQuantity.value
  );
});

// Watch for changes in the shopping cart to sync local quantity
watch(
  () => props.purchase.tickets,
  () => {
    localQuantity.value = currentQuantity.value;
    pendingQuantity.value = null; // Clear pending changes when cart updates
  },
  { immediate: true },
);

const debouncedPendingQuantityChange = debounce(updateQuantity, 500);

watch(pendingQuantity, () => {
  debouncedPendingQuantityChange();
});

async function updateQuantity() {
  if (hasPendingChange.value) {
    await handleUpdateQuantity();
  }
}

function onValueOutOfBounds(originalValue: number, min: number, max: number) {
  if (originalValue > max) {
    outOfBoundsMessage.value = `Only ${max} available. Quantity has been adjusted.`;
    localQuantity.value = max;
    pendingQuantity.value = max;
  }
  if (outOfBoundsMessage.value !== null) {
    setTimeout(() => {
      outOfBoundsMessage.value = null;
    }, 4000);
  }
}

function onQuantityInput(val: number) {
  handleQuantityUpdate(val, props.eventPrice.minimumQuantity);
}

async function handleUpdateQuantity() {
  if (pendingQuantity.value === null) return;

  const quantityDifference = pendingQuantity.value - currentQuantity.value;

  if (quantityDifference === 0) {
    pendingQuantity.value = null;
    return;
  }

  isUpdatingQuantity.value = true;

  try {
    const generalAdmissionToBeAddedList =
      quantityDifference > 0
        ? [
            {
              eventPrice: props.eventPrice,
              quantity: quantityDifference,
            },
          ]
        : [];

    const generalAdmissionToBeRemovedList =
      quantityDifference < 0
        ? [
            {
              eventPrice: props.eventPrice,
              quantity: Math.abs(quantityDifference),
            },
          ]
        : [];

    await onUpdateGACart(
      props.seatNumber,
      generalAdmissionToBeAddedList,
      generalAdmissionToBeRemovedList,
    );

    pendingQuantity.value = null;
  } catch (error) {
    console.error("Failed to update GA cart:", error);
    localQuantity.value = currentQuantity.value;
    pendingQuantity.value = null;
    outOfBoundsMessage.value = "Unable to update quantity. Please try again.";
    setTimeout(() => {
      outOfBoundsMessage.value = null;
    }, 4000);
  } finally {
    isUpdatingQuantity.value = false;
  }
}
</script>

<style scoped>
.mobile-ga-item {
  width: 100%;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
  transition: opacity 0.2s ease;
}

.mobile-ga-item:last-child {
  border-bottom: none;
}

.seat-map-ga-row {
  container-type: inline-size;
  container-name: seat-map-ga-row-container;
}

.seat-map-ga-row-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

@container seat-map-ga-row-container (width > 322px) {
  .seat-map-ga-row-content {
    flex-direction: row;
  }
}

.v-list-item-subtitle {
  -webkit-line-clamp: inherit;
}
.v-list-item-title {
  white-space: normal;
}

.low-inventory-alert {
  color: #1976d2 !important; /* Blue */
  font-weight: 700 !important; /* Bold */
  font-size: 0.9rem;
}
</style>
